import { PublishedResult, Student, SchoolClass, Section } from '../types';

// A single real student can end up represented inconsistently across `published_results`
// docs — older docs predating the `studentId` field, class transfers, duplicate/reassigned
// student records, etc. This mirrors the multi-strategy match already proven in App.tsx's
// StudentPortal wiring (reg no, name+NIN, fuzzy name + enrolled class) so every part of the
// app resolves "which published results belong to this student" the same, reliable way —
// a naive `studentId === student.id` check alone silently drops most historical records.
export const matchesPublishedResult = (
    r: PublishedResult,
    s: Student,
    classes: SchoolClass[],
    sections: Section[]
): boolean => {
    // 1. Direct ID Match
    if (r.studentId === s.id) return true;

    // 1b. Explicit ID Mismatch (CRITICAL FIX for Phantom Records)
    // If the result belongs to a specific student ID that is NOT the current student,
    // we MUST reject it, even if the names match. This handles duplicate names correctly.
    if (r.studentId && r.studentId !== s.id) return false;

    // 2. Reg No Match (Prioritized and Strict)
    if (s.reg && r.reg) {
        const cleanSReg = String(s.reg).trim().replace(/^0+/, '');
        const cleanRReg = String(r.reg).trim().replace(/^0+/, '');
        if (cleanSReg.length > 0 && cleanRReg.length > 0) {
            return cleanSReg === cleanRReg;
        }
    }

    // 3. Strict Name + NIN Match
    if (r.surname?.toLowerCase() === s.surname?.toLowerCase() && r.nin === s.nin) return true;

    // 3b. Reg Number Anchor (Prevent Phantom Name Matches)
    if (s.reg && !r.reg && !r.studentId) {
        const isResultSpecial = (r.className || '').toLowerCase().includes('islam') || (r.className || '').toLowerCase().includes('tahfiz') || (r.sectionName || '').toLowerCase().includes('islam') || (r.sectionName || '').toLowerCase().includes('tahfiz');
        if (!isResultSpecial) return false;
    }

    // 4. Fuzzy Name Match
    const rName = (r.studentName || '').toLowerCase();
    const sFirst = (s.firstName || '').toLowerCase();
    const sSur = (s.surname || '').toLowerCase();
    const rSur = (r.surname || '').toLowerCase();

    const fuzzyNameMatch = (rName.includes(sFirst) && rName.includes(sSur)) ||
        (rSur === sSur && rName.includes(sFirst)) ||
        (rName.includes(sFirst) && (rName.includes(sSur) || sSur.includes(rSur) || rSur.includes(sSur)));

    // 5. Enrolled Class Match (Robust Link) - or Special Section Bypass
    let enrolledClassMatch = false;
    let resultRefClass = classes.find(c => c.id === r.classId);
    if (!resultRefClass && r.className) {
        resultRefClass = classes.find(c => c.name.toLowerCase().trim() === r.className.toLowerCase().trim());
    }
    const resultRefSection = resultRefClass ? sections.find(sec => sec.id === resultRefClass!.sectionId) : undefined;
    const activeSectionName = (r.sectionName || resultRefSection?.name || '').toLowerCase();
    const activeClassName = (r.className || resultRefClass?.name || '').toLowerCase();
    const isSpecialSection = activeSectionName.includes('islamiyya') ||
        activeSectionName.includes('tahfiz') ||
        activeClassName.includes('islamiyya') ||
        activeClassName.includes('islamic') ||
        activeClassName.includes('islam');

    if (s.enrollments && s.enrollments.length > 0) {
        const enrolledClassNames = s.enrollments
            .map(e => classes.find(c => c.id === e.classId)?.name?.toLowerCase())
            .filter(Boolean);

        if (enrolledClassNames.some(name => (r.className || '').toLowerCase().includes(name || '###') || (name || '###').includes((r.className || '').toLowerCase()))) {
            if (isSpecialSection) {
                if (fuzzyNameMatch || rName.includes(sFirst) || rName.includes(sSur)) {
                    enrolledClassMatch = true;
                }
            } else {
                if (fuzzyNameMatch) {
                    enrolledClassMatch = true;
                }
            }
        }
    }

    // FORCE MATCH for Special Sections if Name Matches Partially
    if (isSpecialSection) {
        if (
            rName.includes(sFirst) ||
            rName.includes(sSur) ||
            (s.reg && r.reg && String(s.reg).trim().replace(/^0+/, '') === String(r.reg).trim().replace(/^0+/, ''))
        ) {
            return true;
        }
    }

    return fuzzyNameMatch || enrolledClassMatch;
};

export const getPublishedResultsForStudent = (
    publishedResults: PublishedResult[] | undefined,
    student: Student,
    classes: SchoolClass[] | undefined,
    sections: Section[] | undefined
): PublishedResult[] =>
    (publishedResults || []).filter(r => matchesPublishedResult(r, student, classes || [], sections || []));
